import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { HTTP_NOAUTH } from '../auth/httpclient.def';
import { AuthService } from '../auth/auth.service';
import { environment } from '../../environments/environment';
import { NgProgress } from "ngx-progressbar";


/** Handle to stop waiting for a running job (see CairoService.doJob). */
export interface JobCancel {
    cancelled: boolean;
}

@Injectable()
export class CairoService {
    constructor(
        private auth: AuthService,
        @Inject(HTTP_NOAUTH) private http: HttpClient,
        private ngProgress: NgProgress
    ) { }

    public machines(customer: string) {
        return new Promise((resolve, reject) => {
            if (this.auth.access_token()) {
                const httpOptions = {
                    headers: new HttpHeaders({
                        'X-Client-ID': customer,
                    })
                };

                this.http.get<any[]>(environment.api.cairo + "/machines", httpOptions)
                    .subscribe(
                        (machines: any[]) => {
                            resolve(machines);
                        },
                        error => { console.log(error); reject('request failed') }
                    );
            } else {
                reject('auth error');
            }
        });
    }

    public recipes(customer: string, machine: string) {
        return new Promise((resolve, reject) => {
            if (this.auth.access_token()) {
                const httpOptions = {
                    headers: new HttpHeaders({
                        'X-Client-ID': customer,
                        'X-Machine-ID': machine,
                    })
                };

                this.http.get<any[]>(environment.api.cairo + "/recipes", httpOptions)
                    .subscribe(
                        (recipes: any[]) => {
                            resolve(recipes);
                        },
                        error => { console.log(error); reject('request failed') }
                    );
            } else {
                reject('auth error');
            }
        });
    }

    public substrates(customer: string, machine: string, recipe: string) {
        return new Promise((resolve, reject) => {
            if (this.auth.access_token()) {
                const httpOptions = {
                    headers: new HttpHeaders({
                        'X-Client-ID': customer,
                        'X-Machine-ID': machine,
                    })
                };

                this.http.get<any>(environment.api.cairo + "/recipes/" + recipe, httpOptions)
                    .subscribe(
                        (info: any) => { resolve(info.substrates); },
                        error => { console.log(error); reject('request failed') }
                    );
            } else {
                reject('auth error');
            }
        });
    }

    public layers(customer: string, machine: string, recipe: string) {
        return new Promise((resolve, reject) => {
            if (this.auth.access_token()) {
                const httpOptions = {
                    headers: new HttpHeaders({
                        'X-Client-ID': customer,
                        'X-Machine-ID': machine,
                    })
                };

                this.http.get<any>(environment.api.cairo + "/recipes/" + recipe, httpOptions)
                    .subscribe(
                        (info: any) => { resolve(info.layers); },
                        error => { console.log(error); reject('request failed') }
                    );
            } else {
                reject('auth error');
            }
        });
    }



    spectrophotometer = {
        "shimadzu_rvprove": {
            id: "shimadzu_rvprove",
            name: "Shimadzu UVProbe",
            angle: 15,
            reference: "ED_mirror",
            format: {
                header: 2,
                separator: "\t",
                decimal: "."
            }
        },
        "shimadzu_ascii": {
            id: "shimadzu_ascii",
            name: "Shimadzu ASCII",
            angle: 15,
            reference: "Absolut",
            format: {
                header: 0,
                separator: ",",
                decimal: "."
            }
        },
        "uniwin": {
            id: "uniwin",
            name: "UNIWIN",
            angle: 15,
            reference: "absolut",
            format: {
                header: 2,
                separator: "\t",
                decimal: "."
            }
        },
        "filmetrics": {
            id: "filmetrics",
            name: "Filmetrics",
            angle: 0,
            reference: "absolut",
            format: {
                header: 1,
                separator: "\t",
                decimal: "."
            }
        },
        "shimadzu_tg": {
            id: "shimadzu_tg",
            name: "Shimadzu TG",
            angle: 15,
            reference: "TOG_mirror",
            format: {
                header: 2,
                separator: "\t",
                decimal: "."
            }
        },
        "filmetrics_tpx": {
            id: "filmetrics_tpx",
            name: "Filmetrics TPX",
            angle: 0,
            reference: "absolut100",
            format: {
                header: 1,
                separator: "\t",
                decimal: ","
            }
        },
        "shimadzu_mar": {
            id: "shimadzu_mar",
            name: "Shimadzu MAR",
            angle: 15,
            reference: "MAR_mirror",
            format: {
                header: 2,
                separator: "\t",
                decimal: "."
            }
        },
    }

    public spectrophotometers() {
        return Object.keys(this.spectrophotometer)
            .map(key => this.spectrophotometer[key])
    }

    /** /machines?include=spectrophotometers responses, per customer. */
    private machinesWithSpectros = new Map<string, Promise<any[]>>();

    /**
     * Spectrophotometers offered for a customer's machine: only the ones the
     * `spectrophotometers` section of cairo.json assigns to that
     * customer/machine pair, or all of them when it has none assigned. If the
     * API is an older one (no per-machine list) or fails, all of them too.
     */
    public spectrophotometersFor(customer: string, machine: string): Promise<any[]> {
        const all = this.spectrophotometers();
        if (!this.auth.access_token()) {
            return Promise.resolve(all);
        }

        if (!this.machinesWithSpectros.has(customer)) {
            const httpOptions = {
                headers: new HttpHeaders({
                    'X-Client-ID': customer,
                })
            };
            this.machinesWithSpectros.set(customer,
                this.http.get<any>(environment.api.cairo + "/machines?include=spectrophotometers", httpOptions)
                    .toPromise()
                    .then(resp => (resp && Array.isArray(resp.machines)) ? resp.machines : [])
                    .catch(error => {
                        console.log(error);
                        this.machinesWithSpectros.delete(customer);  // retry next time
                        return [];
                    }));
        }

        return this.machinesWithSpectros.get(customer).then(machines => {
            const m = machines.find(x => x.name === machine);
            const allowed: string[] = m && m.spectrophotometers;
            return allowed ? all.filter(s => allowed.indexOf(s.id) >= 0) : all;
        });
    }

    public angle(spectrophotometer: string): number {
        if (this.spectrophotometer.hasOwnProperty(spectrophotometer)) {
            return this.spectrophotometer[spectrophotometer].angle;
        } else {
            return null;
        }
    }

    public reference(spectrophotometer: string) {
        if (this.spectrophotometer.hasOwnProperty(spectrophotometer)) {
            return this.spectrophotometer[spectrophotometer].reference;
        } else {
            return null;
        }
    }

    public parsing_info(spectrophotometer: string) {
        if (this.spectrophotometer.hasOwnProperty(spectrophotometer)) {
            return this.spectrophotometer[spectrophotometer].format;
        } else {
            return null;
        }
    }

    /**
     * Polling schedule (ms) once the backend answers 202/303. The first
     * probe fires after 1 s, then the delay grows to a 5 s cruise. A
     * `Retry-After` header on any response overrides the next delay.
     */
    private PollDelays: number[] = [1000, 2000, 3000, 5000];
    private MaxWait: number = 120000; // 2min timeout

    private sleep(ms: number): Promise<void> {
        return new Promise<void>(resolve => setTimeout(resolve, ms));
    }

    /** Seconds from a `Retry-After` header converted to ms, or null. */
    private retryAfter(headers: HttpHeaders): number | null {
        const raw = headers ? headers.get('retry-after') : null;
        if (!raw) { return null; }
        const secs = parseFloat(raw);
        return isNaN(secs) ? null : Math.max(0, secs * 1000);
    }

    private async poll(url: string, firstDelay: number | null, cancel: JobCancel): Promise<any> {
        const started = Date.now();
        let attempt = 0;
        let delay = firstDelay != null ? firstDelay : this.PollDelays[0];

        while (Date.now() - started + delay <= this.MaxWait) {
            await this.sleep(delay);
            if (cancel.cancelled) { return { status: false, error: 'cancelled' }; }

            let resp: HttpResponse<Object>;
            try {
                resp = await this.http.get(url, { observe: 'response' }).toPromise();
            } catch (err) {
                // network hiccups (status 0) and 5xx are transient: keep polling
                if (err && (err.status === 0 || err.status >= 500)) {
                    console.log('poll retry after transient error', err.status);
                    resp = null;
                } else {
                    return { status: false, error: err };
                }
            }

            if (cancel.cancelled) { return { status: false, error: 'cancelled' }; }
            if (resp && resp.status === 200) {
                return { status: true, body: resp.body };
            }

            this.ngProgress.inc(.15);
            attempt++;
            const hinted = resp ? this.retryAfter(resp.headers) : null;
            delay = hinted != null
                ? hinted
                : this.PollDelays[Math.min(attempt, this.PollDelays.length - 1)];
        }

        return { status: false, error: 'timeout' };
    }

    /**
     * Runs a job and resolves with its result. Setting `cancel.cancelled`
     * stops polling and resolves with `{ status: false, error: 'cancelled' }`
     * (the backend job itself is not aborted, its result is just dropped).
     */
    public doJob(content: string, cancel: JobCancel = { cancelled: false }): Promise<any> {
        const url = environment.api.cairo + '/';
        const payload = btoa(content);

        return this.http.post(url,
            JSON.stringify({ payload: payload, isBase64Encoded: true }),
            { observe: 'response' }
        ).toPromise()
            .then((resp) => {
                if (cancel.cancelled) {
                    return { status: false, error: 'cancelled' };
                }
                if (resp.status === 200) {
                    return { status: true, body: resp.body };
                }
                if ([202, 303].includes(resp.status)) {
                    return this.poll(resp.headers.get('location'), this.retryAfter(resp.headers), cancel);
                }
                return { status: false, error: 'unexpected status ' + resp.status };
            })
            .catch((err) => {
                console.log(err);
                return { status: false, error: err };
            });
    }
}
